import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from '../app/paths';
import {
  useForgotPassword,
  useJoinLeague,
  useLogin,
  useRegister,
  useResetPassword,
} from '../api/queries';
import { messageFor } from './asyncState';
import type { AuthMode } from '../screens/contracts';
import * as screens from '../ui/screens/player';

/**
 * One route for every authentication state.
 *
 * Login, register, forgot, reset and join are the same form with different fields, and
 * treating them as one screen means a variant designs the entry experience once rather
 * than five times — which is also why they share a mode rather than five components.
 */
export function LoginRoute({ initialMode }: { initialMode: AuthMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ code?: string }>();

  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setMode(initialMode), [initialMode]);

  const login = useLogin();
  const register = useRegister();
  const join = useJoinLeague();
  const forgot = useForgotPassword();
  const reset = useResetPassword();

  const resetToken = new URLSearchParams(location.search).get('token') ?? undefined;

  const goHome = () => navigate(paths.root, { replace: true });

  const handleSubmit: React.ComponentProps<typeof screens.Login>['onSubmit'] = (values) => {
    setError(null);
    setNotice(null);

    const fail = (e: unknown) => setError(messageFor(e));

    switch (mode) {
      case 'login':
        login.mutate(
          { email: values.email ?? '', password: values.password ?? '' },
          { onSuccess: goHome, onError: fail },
        );
        break;

      case 'register':
        register.mutate(
          {
            email: values.email ?? '',
            password: values.password ?? '',
            displayName: values.displayName ?? '',
            inviteCode: values.inviteCode ?? '',
          },
          { onSuccess: goHome, onError: fail },
        );
        break;

      case 'join':
        join.mutate(
          { inviteCode: values.inviteCode ?? '' },
          { onSuccess: goHome, onError: fail },
        );
        break;

      case 'forgot':
        forgot.mutate(
          { email: values.email ?? '' },
          {
            // Deliberately the same message whether or not the address exists, matching
            // the server's refusal to confirm who has an account.
            onSuccess: () =>
              setNotice('If that email has an account, a reset link is on its way.'),
            onError: fail,
          },
        );
        break;

      case 'reset':
        reset.mutate(
          { token: values.token ?? resetToken ?? '', password: values.password ?? '' },
          {
            onSuccess: () => {
              setNotice('Password updated. Sign in with your new password.');
              setMode('login');
            },
            onError: fail,
          },
        );
        break;
    }
  };

  const isSubmitting =
    login.isPending || register.isPending || join.isPending || forgot.isPending || reset.isPending;

  return (
    <screens.Login
      mode={mode}
      isSubmitting={isSubmitting}
      error={error}
      notice={notice}
      onSubmit={handleSubmit}
      onModeChange={(next) => {
        setError(null);
        setNotice(null);
        setMode(next);
        const href =
          next === 'register'
            ? paths.register
            : next === 'forgot'
              ? paths.forgot
              : next === 'reset'
                ? paths.reset
                : paths.login;
        navigate(href, { replace: true });
      }}
      presetInviteCode={params.code ?? null}
      demoHint={
        import.meta.env.DEV
          ? { email: 'commish@otoe.league', password: 'pickem-demo-2026' }
          : null
      }
    />
  );
}
